import { ElementType, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import { useRouter } from 'next/router';
import Header from '@/finsight/ui/common/components/header/FinsightHeader/Header';
import HeaderBanner from '@framework/ui/Organisms/HeaderBanner';
import { useHeaderMenuContext } from '@/creditflowresearch/application/SearchHeaderMenu/HeaderMenuContext';
import StaticHeader from '@/finsight/ui/components/staticPages/StaticPageHeader';
import useElementSize from '@framework/hooks/useElementSize';
import { staticPages } from '@/finsight/ui/components/staticPages/StaticPageHeader/staticPages';
import { HEADER_WRAPPER_ID, STICKY_HEADER_CONTENT_ID } from '../constants';
import { staticPaths } from '@finsight/urls/static';
import { ILinkConfig } from '@/finsight/ui/common/components/header/Header/Links/linksConfig';

import styles from './styles.module.scss';

interface IHeaderLayoutProps {
  LogoComponent?: ElementType<{ onClick: () => void }>,
  isVisibleMarketDataMenu?: boolean,
  isVisibleSearch?: boolean,
  isVisibleLogin?: boolean,
  isIgnoreUserPlan?: boolean,
  linksConfig?: ILinkConfig[],
}

const HeaderLayout = ({
  LogoComponent,
  linksConfig,
}: IHeaderLayoutProps) => {
  const { pathname } = useRouter();
  const isStaticPage = staticPaths.getStaticPagesList().indexOf(pathname) !== -1;
  const [isFixed, setIsFixed] = useState(false);
  const {
    isMobileMenuOpen,
    isMarketDataMenuOpen,
    isSearchResultsOpen,
  } = useHeaderMenuContext();
  const scrollHandler = useCallback(throttle(() => {
    setIsFixed(window.scrollY > 0);
  }, 100), []);
  useEffect(() => {
    if (!isStaticPage) {
      window.addEventListener('scroll', scrollHandler);
    } else {
      setIsFixed(false);
    }
    return () => {
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [pathname]);

  const isOneOfHeaderMenuOpen = isMobileMenuOpen || isMarketDataMenuOpen || isSearchResultsOpen;
  const { height: stickyContentHeight } = useElementSize(STICKY_HEADER_CONTENT_ID);
  return (
    <div
      className={ styles.headerWrapper }
      id={ HEADER_WRAPPER_ID }
    >
      <div className={ cn({
        [styles.menuOverlay]: isOneOfHeaderMenuOpen,
      }) }
      />
      <div
        className={
          cn(styles.headerLayoutContainer, {
            [styles.headerLayoutContainerWithMenu]: isOneOfHeaderMenuOpen,
            [styles.headerLayoutContainerWithMobileMenu]: isMobileMenuOpen,
            [styles.headerLayoutStaticContainer]: isStaticPage,
            [styles.headerLayoutFixedContainer]: isOneOfHeaderMenuOpen && isStaticPage,
          })
        }
      >
        <div className={ cn({
          [styles.menuWrapper]: isOneOfHeaderMenuOpen,
          [styles.marketDataMenuWrapper]: isMobileMenuOpen,
        }) }
        >
          { isFixed && <div style={ { height: stickyContentHeight } } /> }
          <div
            id={ STICKY_HEADER_CONTENT_ID }
            className={ cn(styles.stickyContent, {
              [styles.stickyContentFixed]: isFixed,
              [styles.stickyContentOpened]: isOneOfHeaderMenuOpen,
            }) }
          >
            <HeaderBanner isSticky={ false } />
            <Header
              transparent={ isStaticPage && !isOneOfHeaderMenuOpen }
              LogoComponent={ LogoComponent }
              linksConfig={ linksConfig }
            />
          </div>
        </div>
      </div>
      { isStaticPage && (
        <StaticHeader { ...staticPages[pathname] } />
      ) }
    </div>
  );
};

export default HeaderLayout;
